import type { ClassMember } from "../networking/schoolClasses";
import { toEpochMs } from "../utils/dateUtils";
import { getParsedLocalStorageRecord } from "./LocalStorageHelper";

const STORAGE_KEY = "classMembersLastSeen";

type LastSeenByClass = Record<string, number>;

const readAll = (): LastSeenByClass => getParsedLocalStorageRecord<LastSeenByClass>(STORAGE_KEY);

export const readLastSeen = (classToken: string): number | null => {
    const value = readAll()[classToken];
    return typeof value === "number" ? value : null;
};

export const markSeen = (classToken: string, mark: number): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [classToken]: mark }));
};

/**
 * The mark is the newest join date seen, not the browser clock: join dates are server-local time
 * parsed in the browser zone, so comparing them with Date.now() would hide or re-flag members
 * whenever the two clocks disagree.
 */
export const newestCreatedAt = (members: Pick<ClassMember, "createdDate">[]): number =>
    members.reduce((newest, member) => Math.max(newest, toEpochMs(member.createdDate) ?? 0), 0);

/** No baseline (first visit after release) marks nothing, so pre-existing members never light up. */
export const isNewClassMember = (member: Pick<ClassMember, "createdDate">, lastSeen: number | null): boolean => {
    if (lastSeen === null) return false;
    const created = toEpochMs(member.createdDate);
    return created !== null && created > lastSeen;
};
